import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import IndiaDataGrid from "../../include/IndiaDataGrid";
import { AccountBalanceWallet,CurrencyRupee,Sync,AddCircleOutline,RemoveCircleOutline,} from '@mui/icons-material';
import { Box,Grid,TextField,Typography,Button,Container,DialogActions,} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { GridToolbarExportContainer, GridToolbarColumnsButton, GridToolbarQuickFilter, GridFooterContainer, GridPagination } from '@mui/x-data-grid';

import { callPostApi } from '../../function/ApiCallFunction';
import { toast } from 'react-toastify';

import DialogBox from '../../components/ajax/DialogBox';
import InfoPopover from '../../components/CommanComponents/InfoPopover';
import GradientLoader from '../../components/CommanComponents/GradientLoader';
import { DataContext } from '../../context/DataContext';
import { REACT_ENV } from '../../include/config';
import { formatAmount } from '../../include/money';

const WalletPlusList = () => 
{
    const navigate = useNavigate();
    const location = useLocation();
    const {loginData,setLoginData } = useContext(DataContext);
    const store_id = loginData?.store_info?.store_id; 

    const [allData,        setAllData]        = useState([]);    
    const [pageIndex,      setPageIndex]      = useState(0);
    const [hasMoreData,    setHasMoreData]    = useState(true);
    const [filtersApplied, setFiltersApplied] = useState(false);
    
    const [listLoading,    setListLoading]    = useState(false);
    const [actionLoading,  setActionLoading]  = useState(false);

    const [open,           setOpen]           = useState(false);

    const isInitialSessionLoad = useRef(false);
    const hasFetchedRef        = useRef(false);
    
    const [addAmount, setAddAmount] = useState('')
    const [addDesc,   setAddDesc]   = useState('')
    const [errors,    setErrors]    = useState({});

    /* ------------------ USER ------------------ */
   

    /* ------------------ DATE HELPER ------------------ */
    const getDateNDaysAgo = (n) => {
        const date = new Date();
        date.setDate(date.getDate() - n);
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const day = (`0${date.getDate()}`).slice(-2);
        return `${year}-${month}-${day}`;
    };

    // Default Filter For Searching
    const defaultFilters = useMemo(() => ({store_id, startDate:getDateNDaysAgo(30), endDate:getDateNDaysAgo(0), fetchLimit: '10', pageSize: '10' }), [store_id]);
    const [filters, setFilters] = useState(defaultFilters);
    
    // Reset Data States
    const resetDataStates = () => 
    {
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        hasFetchedRef.current = false;
    };

    // Apply Filters
    const applyFilters = () => 
    {
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        setFiltersApplied(true);
    };

    // Clear Filters
    const clearFilters = () => 
    {
        setFilters(defaultFilters);
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        sessionStorage.setItem('wallePlusHistListFilter', JSON.stringify(defaultFilters));
        setFiltersApplied(true);
    };

    /* ---------------- SESSION FILTER ---------------- */
    useEffect(() => 
    {
        const storedState = sessionStorage.getItem('wallePlusHistListFilter');
        if (!storedState) return;

        try 
        {
          const parsed = JSON.parse(storedState);
          setFilters({ ...defaultFilters, ...parsed });
          isInitialSessionLoad.current = true;
          setFiltersApplied(true);
        } 
        catch { sessionStorage.removeItem("wallePlusHistListFilter"); }
    }, [defaultFilters]);


    // Fetch Data Function
    const fetchData = useCallback(async (page = pageIndex) => 
    {
        if (listLoading || !hasMoreData) return;

        setListLoading(true);
        try 
        {
            const StartNum = page * Number(filters.fetchLimit);
            const payload = {mod: "STORE_WALLET_HISTORY", data_arr: {...filters, start: StartNum, limit: filters.fetchLimit,}};                               
            const response = await callPostApi('store-wallet', payload);

            if (response.status === '200' && response.data) 
            {
                const apiResponse = response.data;

                if (apiResponse.length > 0) 
                {
                    setAllData(prev => [...prev, ...apiResponse]);
                    setPageIndex(page);
                } 
                else {  setHasMoreData(false); }
                
                if (apiResponse.length < filters.fetchLimit) { setHasMoreData(false); }
            } 
            else  { setHasMoreData(false); }
        } 
        catch (error) { setHasMoreData(false); } 
        finally { setListLoading(false); }

    }, [filters, pageIndex, listLoading, hasMoreData]);

    /* ---------------- INITIAL FETCH ---------------- */
    useEffect(() => 
    {
        if (!filtersApplied && !hasFetchedRef.current) 
        {
            hasFetchedRef.current = true;
            fetchData(0);
        }
    }, [fetchData, filtersApplied]);

    /* ------------------ FILTER APPLIED ------------------ */
    useEffect(() => 
    {
        if (!filtersApplied) return;

        sessionStorage.setItem('wallePlusHistListFilter', JSON.stringify(filters));

        resetDataStates();
        fetchData(0);
        setFiltersApplied(false);
        isInitialSessionLoad.current = false;
    }, [filtersApplied, filters, fetchData]);

    /* ------------------ Refresh Wallet ------------------ */
    const refreshPage = useCallback(async () =>
    {
        setActionLoading(true);
        try 
        {
            const payload = { mod: "STORE_DETAIL", data_arr: { store_id },};
            const apiResult = await callPostApi("store-login", payload);

            if (apiResult?.status === 200 || apiResult?.status === "200") 
            {
                setLoginData(prev => 
                {
                    const updated = {...prev, store_info: apiResult.data.store_info };
                    sessionStorage.setItem('loginData', JSON.stringify(updated));
                    return updated;
                });

                window.dispatchEvent(new Event('refreshNavbar'));
                window.dispatchEvent(new Event('refreshPriceTable'));
            } 
        } 
        finally { setActionLoading(false); } 
    },[store_id,setLoginData]);

    useEffect(() => { refreshPage(); }, [refreshPage]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const payStatus = params.get("pay_status");
        if (!payStatus) return;
        if (payStatus === "success") {
            toast.success("Wallet recharged");
            refreshPage();
        } else {
            toast.info("Payment was not completed");
        }
        navigate(location.pathname, { replace: true });
    }, [location.search, location.pathname, navigate, refreshPage]);

    /* ---------------- ADD AMOUNT ---------------- */

    const validate = () => 
    {
        const newErrors = {};
        if (!addAmount || isNaN(addAmount) || Number(addAmount) <= 0) { newErrors.addAmount= 'Enter a valid Amount';}

        if (!addDesc) { newErrors.addDesc= 'Enter Description'; }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };
            
    /* ------------------ Add Amount API ------------------ */
    const addAmountApi = async () => 
    {
        if (!validate()) return;

        try 
        {
          setActionLoading(true);
          
          const payload = { mod: "WALLET_RECHARGE_LINK", data_arr: { store_id, amount: addAmount, pay_desc :addDesc, env: REACT_ENV, return_path: location.pathname } };
          const apiResult = await callPostApi("store-wallet", payload);

          if (apiResult?.status === 200 || apiResult?.status === "200") 
          {
              const payment_link = apiResult.data.payment_link;

              if (String(REACT_ENV) === "LIVE") {
                  window.location.assign(payment_link);
                  return;
              }

              toast.success(apiResult?.data?.message || "Payment link created");
              window.open(payment_link, '_blank');

              setOpen(false);
              setAddAmount('');
              setAddDesc('');
              setErrors({});              
          } 
          else { toast.info(apiResult?.status_message || "Something went wrong"); } 
        } 
        catch (error) { toast.error("Failed to create payment link"); } 
        finally { setActionLoading(false); }
    };

    const CustomToolbar = () => {
        return (
            <GridToolbarExportContainer sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
                <GridToolbarColumnsButton /><GridToolbarQuickFilter debounceMs={300} />
            </GridToolbarExportContainer>
        );
    };

    const CustomFooterWithLoadMore = ({ hasMoreData, loading, onLoadMore }) => (
      <GridFooterContainer sx={{ px: 2, py: 1, overflowX: 'hidden', }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', width: '100%', }} >
          
              {/* Left: Rows per page */}
              <Box sx={{ flex: 1 }} />
              {/* Center: Load More */}
              
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  {hasMoreData ? (
                      <Button variant="contained" onClick={onLoadMore} disabled={loading} sx={{ minWidth: 120 }}>
                          {loading ? 'Loading...' : 'Load More'}
                      </Button>
                  ) : (<Typography variant="body2" color="text.secondary">No more records </Typography>)}
              </Box>

              {/* Right: Pagination - Increased width */}
              <Box sx={{ flex: 2, minWidth: 250, display: 'flex', justifyContent: 'flex-end' }}>
                  <GridPagination />
              </Box>
              
          </Box>
      </GridFooterContainer>
    );

    const fetchLimits    = [ "5", "10", "20", "50", "100", "250", "500", "1000"];
                  
  return (
    <Container maxWidth="xl" sx={{ my: 2 }}>
        {(listLoading || actionLoading) && <GradientLoader text="Loading" />}

       <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
         <Button variant="contained" sx={{ backgroundColor: '#000000', color: '#ffffff' }}
          onClick={() =>  {  if (window.history.length > 2) { navigate(-1); } else { navigate("/home"); }} }> Back </Button>
      </Box>

      <Typography variant="h6"  gutterBottom sx={{ background: 'linear-gradient(195deg, #49a3f1, #1A73E8)',
          p: 1, color: '#ffffff',  borderRadius: '4px', mb: 3,}}> <AccountBalanceWallet/>  Wallet </Typography>
      
       <Box sx={{backgroundColor:'#f2f2f2', borderRadius:'8px',boxShadow: 'rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px;'}}>
          
          <Typography sx={{px:2, py:1}} ><Sync onClick ={() => refreshPage()}/> </Typography>
          
          <Grid container spacing={2} sx={{backgroundColor:'#ffffff', borderTopLeftRadius:'12px', borderTopRightRadius:'12px', borderBottomRightRadius:'8px',
               borderBottomLeftRadius:'8px', boxShadow: 'rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px;', py:2 ,px:2}}>

              <Grid item size={{ xs: 12,sm:12, md: 3,  }}>
                
                  <Typography sx={{mb:1, color:'#888'}}>Wallet Balance <InfoPopover triggerTabIndex={-1} title="Wallet Balance"  
                      description="Available funds in your Cashi wallet that can be used for generating Coupons." />
                  </Typography>

                  <Box sx={{display:'flex', alignItems:'center'}}>
                    <CurrencyRupee sx={{fontSize:'35px',color:'#0a32d4ff'}} /> 
                    <Typography variant="h4" sx={{color:'#0a32d4ff'}}>{formatAmount(loginData?.store_info?.wallet_amount)}</Typography>
                  </Box> 

                  <Box sx={{display:'flex', my:3}}>
                    <Button variant="contained" sx={{background:'linear-gradient(195deg, #66BB6A, #43A047)'}} onClick={() => setOpen(true)}>Add Balance</Button>
                  </Box>

              </Grid>

              <Grid item size={{ xs: 12,sm:12, md: 3,  }} sx={{borderLeft:'1px solid #ccc', px:2,}}>
                  
                  <Typography sx={{mb:1, color:'#888'}}>Reserve Balance <InfoPopover triggerTabIndex={-1} title="Reserve Balance"  
                    description="Reserve amount in your Cashi wallet that can be use for release amount to customer" /> </Typography>

                  <Box sx={{display:'flex', alignItems:'center'}}>
                      <CurrencyRupee sx={{fontSize:'35px',color:'#098665'}} /> 
                      <Typography onClick={() => navigate('/dashboardPlus/LoadnActiveQR')}variant="h4" 
                        sx={{ cursor: 'pointer',color:'#098665'}}>{formatAmount(loginData?.store_info?.reserve_amount)}</Typography>
                  </Box> 

              </Grid>

              <Grid item size={{ xs: 12,sm:12, md: 3,  }} sx={{borderLeft:'1px solid #ccc', px:2,}}>
                  <Typography sx={{mb:1, color:'#888'}}>Disbursed Amount <InfoPopover triggerTabIndex={-1} title="Disbursed Amount"  
                      description="Disbursed amount to customer" /> </Typography>

                  <Box sx={{display:'flex', alignItems:'center'}}> <CurrencyRupee sx={{fontSize:'35px',color:'#098665'}} /> 
                    <Typography  onClick={() => navigate('/dashboardPlus/redeemed')} variant="h4" 
                        sx={{cursor: 'pointer',color:'#098665'}}>{formatAmount(loginData?.store_info?.paid_amount)}</Typography>
                  </Box> 
              </Grid>

              <Grid item size={{ xs: 12,sm:12, md: 3,  }} sx={{borderLeft:'1px solid #ccc', px:2,}}>
                  <Typography sx={{mb:1, color:'#888'}}>Commission Amount <InfoPopover triggerTabIndex={-1} title="Commission Amount"  
                      description="Commission amount to Cashi" /> </Typography>

                  <Box sx={{display:'flex', alignItems:'center'}}> <CurrencyRupee sx={{fontSize:'35px',color:'#098665'}} /> 
                    <Typography  onClick={() => navigate('/dashboardPlus/redeemed')} variant="h4" 
                        sx={{cursor: 'pointer',color:'#098665'}}>{formatAmount(loginData?.store_info?.commission_amount)}</Typography>
                  </Box> 
              </Grid>

          </Grid>

       </Box>  

       <Box sx={{my:2}}> <div>
       
               <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
               
                   <Button sx={{background: "linear-gradient(195deg, #42424a, #191919)",color: "#ffffff", ml:'auto',
                       boxShadow:"rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px", }}onClick={clearFilters}>Clear Filters</Button>
           
               </Box>
       
               <div className='mb-3'>
                   <div className="row align-items-center g-2 searchFilter">
                                      
                       {/* Issued Date */}
                       <div className="col-12 col-md-2">
                           <input type="date" className="form-control" placeholder="Start Date" value={filters.startDate}
                               onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))} />
                       </div>
       
                       <div className="col-12 col-md-2">
                           <input type="date" className="form-control" placeholder="End Date" value={filters.endDate}
                               onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}  />
                       </div>
       
                       {/* Page Size Filter */}  
                       <div className="col-12 col-md-2">
                           <select className="form-control" value={filters.fetchLimit} onChange={(e) => setFilters(prev => ({ ...prev, fetchLimit: e.target.value }))} >
                               <option value="">Record Fetch Limit</option>
                               {fetchLimits.map(value => ( <option key={value} value={value}>{value}</option>))}
                           </select>
                       </div>  
       
                       {/* Apply flter */}
                       <div className="col-12 col-md-2 ms-auto">                                        
                           <Button  sx={{background:'linear-gradient(195deg, #49a3f1, #1A73E8)', color:'#ffffff',ml:1,
                               boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px' }}
                                   style={{float: 'right'}}  onClick={() => { resetDataStates(); applyFilters(); }} >Apply Filters</Button>
                       </div>  
                       
                   </div>
               </div>
               
               <Box sx={{ height: 'auto', width: '100%' }}>
                   <IndiaDataGrid rows={allData.map((row, i) => ({...row,srNo: i + 1 ,id: row.row_temp_id || `${pageIndex}-${i}`}))}
                       columns={[
                           { field: 'srNo', headerName: 'Sr. No.', flex: 0.5, sortable: false, filterable: false,},
                           { field: 'opening_balance', headerName: 'Opening Balance', flex:  1, minWidth: 120,
                               renderCell: (params) => 
                               (
                                   <><CurrencyRupee sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '16px', mb: '2px' }} />{formatAmount(params.value)} </>
                               ),
                           }, 
                           { field: 'wallet_amount', headerName: 'Transactions', flex:  1, minWidth: 120,
                               renderCell: (params) => 
                               {
                                   const isAdd = params.row.wallet_action === 'Add';
                                   const isSub = params.row.wallet_action === 'Sub';
                                   return (
                                       <>
                                           <Box sx={{ display: 'flex', alignItems: 'center', color: isAdd ? 'green' : isSub ? 'red' : 'inherit' }}>
                                           {isAdd && <AddCircleOutline sx={{ fontSize: 18, mr: 0.5 }} />}
                                           {isSub && <RemoveCircleOutline sx={{ fontSize: 18, mr: 0.5 }} />}
                                           <CurrencyRupee sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '16px', mb: '2px' }} />
                                           {formatAmount(params.value)}
                                       </Box>
                                   </>           
                                   )                        
                               },
                           }, 
                           { field: 'closing_balance', headerName: 'Closing Balance', flex: 1, minWidth: 120,
                               renderCell: (params) => (
                               <>
                                   <CurrencyRupee sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '16px', mb: '2px' }} />
                                   {formatAmount(params.value)}
                               </>
                               ),
                           },                    
                           { field: 'comment',    headerName: 'Remarks', flex: 2, minWidth: 120, },
                           { field: 'date_added', headerName: 'Date', flex: 1, minWidth: 120,  },                    
                         ]}
                       
                         localeText={{ noRowsLabel: "No records found", noResultsOverlayLabel: "No matching data found", }}
                       pageSize={Number(filters.pageSize)}
                       onPageSizeChange={(newSize) => setFilters((prev) => ({ ...prev, pageSize: String(newSize) })) }
                       pageSizeOptions={[10, 15, 20, 25, 40, 50, 80, 100]}
                       pagination
                       disableSelectionOnClick 
                       initialState={{pagination: {paginationModel: {pageSize: Number(filters.pageSize),page: 0,},},}}
                       slots={{toolbar: CustomToolbar, footer: () => ( <CustomFooterWithLoadMore onLoadMore={() => fetchData(pageIndex + 1)} 
                       hasMoreData={hasMoreData} loading={listLoading} /> ), }}
                       slotProps={{toolbar: {showQuickFilter: true,quickFilterProps: { debounceMs: 300 },},}}
                       sx={{ '& .MuiDataGrid-cell': {fontSize: '13px', }, 
                           '& .MuiDataGrid-columnHeaderTitle': {fontSize: '15px',fontWeight: 'bold', whiteSpace: 'normal !important',lineHeight: '1.5', textAlign: 'center',},
                           "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {outline: "none"} }}
                       rowHeight={45} headerHeight={45}
                       
                   />
               </Box>
       
           </div> </Box>   
       
       {/* Add balance */}
       <DialogBox open={open} onClose={() => setOpen(false)} title="Add Balance">
         
          <TextField className="transparent-textfield" fullWidth margin="normal" label="Enter Amount" required  value={addAmount}  
            onChange={(e) =>  { setAddAmount(e.target.value); setErrors(prev => ({ ...prev, addAmount: undefined }));}}
            error={!!errors.addAmount} helperText={errors.addAmount} type="number" inputProps={{ min: 1 }}/>

          <TextField className="transparent-textfield" fullWidth margin="normal" label="Remark for bank *" value={addDesc}  
            onChange={(e) =>  { setAddDesc(e.target.value); setErrors(prev => ({ ...prev, addDesc: undefined })); }}
            error={!!errors.addDesc} helperText={errors.addDesc} type="text" inputProps={{ min: 1 }}/>

          <DialogActions>
            <Button onClick={() => setOpen(false)} color="primary"> Cancel </Button>
            <Button  color="primary" variant="contained"  onClick={ addAmountApi} > Ok </Button>
          </DialogActions>

      </DialogBox> 
                    
    </Container>
  )
}

export default WalletPlusList;